import type { AiFile } from '@farmos/shared';
import { shrinkImage } from '@farmos/ui/image';

const MAX_BYTES = 10 * 1024 * 1024;

export const AI_ACCEPT =
  'image/jpeg,image/png,image/webp,image/heic,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function toBase64(buf: ArrayBuffer): string {
  let bin = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i += 0x8000)
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

export async function toAiFile(file: File): Promise<AiFile> {
  if (file.size > MAX_BYTES * 3) throw new Error(`${file.name}: file too large`);
  if (file.type.startsWith('image/')) {
    const blob = await shrinkImage(file);
    return { type: 'image/jpeg', data: toBase64(await blob.arrayBuffer()) };
  }
  if (file.size > MAX_BYTES) throw new Error(`${file.name}: file too large`);
  const type = file.name.toLowerCase().endsWith('.csv')
    ? 'text/csv'
    : file.name.toLowerCase().endsWith('.xlsx')
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : file.type;
  if (type !== 'application/pdf' && type !== 'text/csv' && !type.includes('spreadsheetml')) {
    throw new Error(`${file.name}: unsupported file type`);
  }
  return { type: type as AiFile['type'], data: toBase64(await file.arrayBuffer()) };
}

/**
 * Photos are stored downscaled (≈200–400 KB instead of several MB) so the free 1 GB
 * Supabase Storage lasts for years. PDFs and non-images are stored as-is.
 */
export async function prepareUpload(
  file: File,
): Promise<{ body: Blob; contentType: string; ext: string }> {
  if (file.type.startsWith('image/')) {
    try {
      return { body: await shrinkImage(file), contentType: 'image/jpeg', ext: 'jpg' };
    } catch {
      /* e.g. HEIC the browser can't decode: fall through and keep the original */
    }
  }
  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'bin';
  return { body: file, contentType: file.type || 'application/octet-stream', ext };
}
